import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Settings as SettingsIcon, Activity, DollarSign, BarChart3, Sparkles, AlertCircle, CheckCircle, Bell, Star, Target, Clock, Zap, Play, Lock, Brain } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import Login from './Login';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [settings, setSettings] = useState(null);
  const [currentTheme, setCurrentTheme] = useState(localStorage.getItem('app-theme') || 'neon');
  const [dashboardBannerDismissed, setDashboardBannerDismissed] = useState(
    localStorage.getItem('dashboard-banner-dismissed') === 'true'
  );
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Scanner results filters
  const [allScanResults, setAllScanResults] = useState([]);
  const [selectedScanIndex, setSelectedScanIndex] = useState(0);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterReason, setFilterReason] = useState('all');
  const [searchSymbol, setSearchSymbol] = useState('');
  
  // Theme management
  const themes = {
    neon: {
      name: 'Neon Trader',
      description: 'Modern AI aesthetic with cyan glow',
      primary: '#00D9FF',
      background: '#0a0e27',
      accent: '#B84FFF'
    },
    bloomberg: {
      name: 'Bloomberg',
      description: 'Classic Wall Street terminal',
      primary: '#00FF41',
      background: '#000000',
      accent: '#FF6B35'
    },
    gold: {
      name: 'Gold Rush',
      description: 'Premium luxury trading',
      primary: '#FFD700',
      background: '#0B1426',
      accent: '#E5E5E5'
    }
  };

  const applyTheme = (themeName) => {
    const theme = themes[themeName];
    if (!theme) return;

    const root = document.documentElement;
    
    if (themeName === 'neon') {
      // Neon Trader (current)
      root.style.setProperty('--background', '222 47% 4%');
      root.style.setProperty('--foreground', '180 100% 90%');
      root.style.setProperty('--primary', '180 100% 50%');
      root.style.setProperty('--neon-cyan', '180 100% 50%');
      root.style.setProperty('--neon-purple', '280 100% 65%');
      root.style.setProperty('--accent', '280 100% 65%');
    } else if (themeName === 'bloomberg') {
      // Bloomberg Terminal
      root.style.setProperty('--background', '0 0% 0%');
      root.style.setProperty('--foreground', '120 100% 50%');
      root.style.setProperty('--primary', '120 100% 50%');
      root.style.setProperty('--neon-cyan', '120 100% 50%');
      root.style.setProperty('--neon-purple', '15 100% 60%');
      root.style.setProperty('--accent', '15 100% 60%');
    } else if (themeName === 'gold') {
      // Gold Rush
      root.style.setProperty('--background', '220 50% 8%');
      root.style.setProperty('--foreground', '45 100% 90%');
      root.style.setProperty('--primary', '45 100% 50%');
      root.style.setProperty('--neon-cyan', '45 100% 50%');
      root.style.setProperty('--neon-purple', '0 0% 90%');
      root.style.setProperty('--accent', '0 0% 90%');
    }
    
    setCurrentTheme(themeName);
    localStorage.setItem('app-theme', themeName);
  };

  // Apply theme on mount
  useEffect(() => {
    applyTheme(currentTheme);
  }, []);
  
  const [accountInfo, setAccountInfo] = useState(null);
  const [positions, setPositions] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [enhancedPositions, setEnhancedPositions] = useState([]); // NEW - positions with days held, stops, etc.
  const [aiSignals, setAiSignals] = useState([]);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('info');
  const [currentUsername, setCurrentUsername] = useState(localStorage.getItem('username') || '');
  const isAdmin = currentUsername === 'admin' || currentUsername === 'pkadmin';

  // Login history (admin-only Activity tab)
  const [loginHistory, setLoginHistory] = useState([]);
  const [loginHistoryLoading, setLoginHistoryLoading] = useState(false);
  const [loginHistoryFilter, setLoginHistoryFilter] = useState('all'); // 'all' | 'success' | 'failed'

  // Configuration form
  const [apiKey, setApiKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [riskLevel, setRiskLevel] = useState('medium');

  // Trading form
  const [tradeSymbol, setTradeSymbol] = useState('AAPL');
  const [tradeQuantity, setTradeQuantity] = useState('1');
  const [tradeSide, setTradeSide] = useState('buy');

  // Watchlist & Alerts
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistSignals, setWatchlistSignals] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [exitRules, setExitRules] = useState([]);
  const [scannerResults, setScannerResults] = useState(null);
  
  // Exit rule form
  const [stopLossPercent, setStopLossPercent] = useState('2');
  const [takeProfitPercent, setTakeProfitPercent] = useState('5');
  const [trailingStopPercent, setTrailingStopPercent] = useState('5');
  const [maxHoldDays, setMaxHoldDays] = useState('7');
  
  // Automation
  const [autoTradingEnabled, setAutoTradingEnabled] = useState(false);
  const [dailyBudget, setDailyBudget] = useState('1000');
  const [maxPositionsPerDay, setMaxPositionsPerDay] = useState('2');
  const [automationStatus, setAutomationStatus] = useState(null);
  
  // Analytics state (4 NEW VIEWS)
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [analyticsPortfolio, setAnalyticsPortfolio] = useState(null);
  const [analyticsPerStock, setAnalyticsPerStock] = useState(null);
  const [analyticsAI, setAnalyticsAI] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      checkConfiguration();
    }
  }, [isAuthenticated]);

  const checkAuth = () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsAuthenticated(true);
      setCurrentUsername(localStorage.getItem('username') || '');
    }
  };

  const handleLoginSuccess = () => {
    const token = localStorage.getItem('auth_token');
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setIsAuthenticated(true);
    setCurrentUsername(localStorage.getItem('username') || '');
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('username');
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setCurrentUsername('');
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      toast.success('✅ Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error('Failed to change password: ' + (error.response?.data?.detail || error.message));
    }
  };

  useEffect(() => {
    if (isConfigured && activeTab === 'dashboard') {
      // Small delay to allow backend to fully initialize Alpaca clients
      const timer = setTimeout(() => {
        fetchDashboardData();
        fetchScannerResults();
        fetchEnhancedPositions(); // NEW
        // Only sync positions if authenticated (don't error on login page)
        if (isAuthenticated) {
          syncPositions();
        }
      }, 500);
      
      const interval = setInterval(fetchDashboardData, 30000); // Refresh every 30s
      return () => {
        clearTimeout(timer);
        clearInterval(interval);
      };
    }
  }, [isConfigured, activeTab, isAuthenticated]);

  useEffect(() => {
    if (isConfigured) {
      fetchWatchlist();
      fetchAlerts();
      fetchExitRules();
      fetchAutomationStatus();
      checkExitRules(); // Check on load
      const interval = setInterval(() => {
        checkExitRules(); // Check every minute
        fetchAlerts();
        fetchAutomationStatus();
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [isConfigured]);

  const fetchAutomationStatus = async () => {
    try {
      const response = await axios.get(`${API}/automation/status`);
      setAutomationStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch automation status:', error);
    }
  };

  const fetchScannerResults = async () => {
    try {
      const response = await axios.get(`${API}/scanner/last-results`);
      if (response.data.status !== 'no_results') {
        setScannerResults(response.data);
      }
    } catch (error) {
      console.error('Scanner results error:', error);
    }
  };

  const fetchAllScanResults = async () => {
    try {
      const response = await axios.get(`${API}/scanner/all-results?limit=10`);
      setAllScanResults(response.data || []);
    } catch (error) {
      console.error('Error fetching all scan results:', error);
    }
  };

  // Fetch all scans when Scanner Results tab is active
  useEffect(() => {
    if (isConfigured && activeTab === 'scanresults') {
      fetchAllScanResults();
    }
  }, [isConfigured, activeTab]);
  
  // Fetch analytics when Analytics tab is active (NEW)
  useEffect(() => {
    if (isConfigured && activeTab === 'analytics') {
      fetchAnalyticsData();
    }
  }, [isConfigured, activeTab]);

  // Fetch login history when Activity tab is active (admin only)
  useEffect(() => {
    if (isAuthenticated && isAdmin && activeTab === 'activity') {
      fetchLoginHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAdmin, activeTab, loginHistoryFilter]);

  const handleToggleAutomation = async () => {
    try {
      await axios.post(`${API}/settings`, {
        auto_trading_enabled: !autoTradingEnabled,
        daily_budget: parseFloat(dailyBudget),
        max_positions_per_day: parseInt(maxPositionsPerDay),
        stop_loss_percent: parseFloat(stopLossPercent),
        take_profit_percent: parseFloat(takeProfitPercent),
        trailing_stop_percent: parseFloat(trailingStopPercent),
        trailing_stop_enabled: true
      });
      setAutoTradingEnabled(!autoTradingEnabled);
      toast.success(autoTradingEnabled ? 'Automation disabled' : 'Automation enabled!');
      fetchAutomationStatus();
    } catch (error) {
      toast.error('Failed to toggle automation');
    }
  };

  const handleRunAutomationNow = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/automation/run`);
      const traded = response.data.trading.executed_trades?.length || 0;
      const exited = response.data.exits.auto_exits?.length || 0;
      
      if (traded > 0 || exited > 0) {
        toast.success(`Automation complete: ${traded} trades executed, ${exited} positions exited`);
      } else {
        toast.info('Automation ran - no trades executed (no BUY signals or exit triggers)');
      }
      
      fetchDashboardData();
      fetchScannerResults();
      fetchAlerts();
    } catch (error) {
      toast.error('Automation failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleStartTradingToday = async () => {
    try {
      await axios.post(`${API}/automation/start-today`);
      toast.success('🚀 Trading started for today!');
      fetchAutomationStatus();
      fetchDashboardData();
      fetchScannerResults();
    } catch (error) {
      toast.error('Failed to start trading');
    }
  };

  const handleStopTradingToday = async () => {
    try {
      await axios.post(`${API}/automation/stop-today`);
      toast.success('Trading stopped for today');
      fetchAutomationStatus();
    } catch (error) {
      toast.error('Failed to stop trading');
    }
  };

  const handleRunScanner = async () => {
    try {
      setLoading(true);
      const startResp = await axios.post(`${API}/scanner/trigger-midday`);
      const startData = startResp.data;

      if (startData.status === 'already_running') {
        toast.info('Scanner is already running — watching progress...');
      } else if (startData.status === 'not_active' || startData.status === 'disabled') {
        toast.warning(startData.message || 'Scanner not active');
        setLoading(false);
        return;
      } else {
        toast.info('🔍 Scan started — running in background (may take 1–3 min)', { duration: 4000 });
      }

      // Poll /scanner/status until running=false, then refresh dashboard
      const startedAt = Date.now();
      const MAX_WAIT_MS = 5 * 60 * 1000; // 5 min safety cap
      const POLL_MS = 5000;

      const poll = async () => {
        try {
          const statusResp = await axios.get(`${API}/scanner/status`);
          const { running, latest_result } = statusResp.data;
          if (!running) {
            const trades = latest_result?.executed?.length || 0;
            const candidates = latest_result?.candidates?.length || 0;
            if (trades > 0) {
              toast.success(`✅ Scanner executed ${trades} trades!`);
            } else if (candidates > 0) {
              toast.info(`Found ${candidates} candidates — AI was selective, no buys`);
            } else {
              toast.info('Scan complete — no swing setups right now');
            }
            fetchDashboardData();
            fetchScannerResults();
            fetchAlerts();
            setLoading(false);
            return;
          }
          if (Date.now() - startedAt > MAX_WAIT_MS) {
            toast.warning('Scan still running after 5 min — check Scanner tab later');
            setLoading(false);
            return;
          }
          setTimeout(poll, POLL_MS);
        } catch (err) {
          toast.error('Lost connection while polling scanner status');
          setLoading(false);
        }
      };
      setTimeout(poll, POLL_MS);
    } catch (error) {
      toast.error('Scanner error: ' + (error.response?.data?.detail || error.message));
      setLoading(false);
    }
  };

  const handleEmergencyStop = async () => {
    if (!window.confirm('🚨 EMERGENCY STOP: This will immediately disable ALL trading. Are you sure?')) {
      return;
    }
    
    try {
      setLoading(true);
      await axios.post(`${API}/automation/emergency-stop`);
      toast.success('🚨 Emergency stop activated - All trading disabled');
      fetchAutomationStatus();
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to execute emergency stop: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleResetCounter = async () => {
    if (!window.confirm('Reset position counter to 0/10? This will clear today\'s trade history.')) {
      return;
    }
    
    try {
      await axios.post(`${API}/automation/reset-counter`);
      toast.success('✅ Counter reset to 0/10');
      fetchAutomationStatus();
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to reset counter: ' + (error.response?.data?.detail || error.message));
    }
  };

  const syncPositions = async () => {
    try {
      const response = await axios.post(`${API}/positions/sync`);
      const data = response.data;
      
      if (data.created_rules > 0) {
        toast.success(`✅ Synced ${data.total_positions} positions. Created ${data.created_rules} exit rules.`);
      } else if (data.total_positions > 0) {
        // Silent success for already-synced positions
        console.log(`Synced ${data.total_positions} positions (rules already exist)`);
      }
      
      fetchExitRules();
      fetchAlerts();
      fetchDashboardData();
      fetchScannerResults();
    } catch (error) {
      // Only show error if not auth issue (401) - silent fail on background sync
      if (error.response?.status !== 401) {
        console.error('Sync error:', error);
        toast.error('Failed to sync positions: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const checkConfiguration = async () => {
    try {
      const response = await axios.get(`${API}/health`);
      setIsConfigured(response.data.alpaca_configured);
      if (response.data.alpaca_configured) {
        fetchSettings();
      }
    } catch (error) {
      console.error('Health check failed:', error);
    }
  };
  
  // Fetch analytics data (4 NEW COMPREHENSIVE VIEWS)
  const fetchAnalyticsData = async () => {
    try {
      const [overviewResp, portfolioResp, perStockResp, aiResp] = await Promise.all([
        axios.get(`${API}/analytics/overview`).catch(() => null),
        axios.get(`${API}/analytics/portfolio`).catch(() => null),
        axios.get(`${API}/analytics/per-stock`).catch(() => null),
        axios.get(`${API}/analytics/ai-performance`).catch(() => null)
      ]);
      
      if (overviewResp) setAnalyticsOverview(overviewResp.data);
      if (portfolioResp) setAnalyticsPortfolio(portfolioResp.data);
      if (perStockResp) setAnalyticsPerStock(perStockResp.data);
      if (aiResp) setAnalyticsAI(aiResp.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      setRiskLevel(response.data.risk_level);
      setAutoTradingEnabled(response.data.auto_trading_enabled || false);
      setDailyBudget(response.data.daily_budget?.toString() || '1000');
      setMaxPositionsPerDay(response.data.max_positions_per_day?.toString() || '2');
      setStopLossPercent(response.data.stop_loss_percent?.toString() || '2');
      setTakeProfitPercent(response.data.take_profit_percent?.toString() || '20');
      setTrailingStopPercent(response.data.trailing_stop_percent?.toString() || '5');
      setApiKey(response.data.alpaca_api_key || '');
      setSecretKey(response.data.alpaca_secret_key || '');
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [accountRes, positionsRes, pendingRes, signalsRes, historyRes] = await Promise.all([
        axios.get(`${API}/account/info`).catch((err) => {
          console.error('Account info fetch failed:', err.message);
          return null;
        }),
        axios.get(`${API}/account/positions`).catch((err) => {
          console.error('Positions fetch failed:', err.message);
          return { data: [] };
        }),
        axios.get(`${API}/account/pending-orders`).catch((err) => {
          console.error('Pending orders fetch failed:', err.message);
          return { data: [] };
        }),
        axios.get(`${API}/ai/signals?limit=5`).catch(() => ({ data: [] })),
        axios.get(`${API}/trading/history?limit=10`).catch(() => ({ data: [] }))
      ]);

      if (accountRes) setAccountInfo(accountRes.data);
      
      // Fix: /account/positions returns array directly, not object with .positions property
      const positionsData = Array.isArray(positionsRes.data) ? positionsRes.data : [];
      console.log('🔍 fetchDashboardData - Raw response:', positionsRes);
      console.log('🔍 fetchDashboardData - Positions array:', positionsData);
      console.log('🔍 fetchDashboardData - Setting positions state with', positionsData.length, 'items');
      
      setPositions(positionsData);
      setPendingOrders(Array.isArray(pendingRes.data) ? pendingRes.data : []);
      setAiSignals(signalsRes.data);
      setTradeHistory(historyRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  };

  // Fetch enhanced positions with exit rules and next actions (NEW)
  const fetchEnhancedPositions = async () => {
    try {
      const response = await axios.get(`${API}/positions/enhanced`);
      setEnhancedPositions(response.data);
    } catch (error) {
      console.error('Error fetching enhanced positions:', error);
    }
  };

  const handleSaveSettings = async () => {
    if (!apiKey || !secretKey) {
      toast.error('Please enter both API Key and Secret Key');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/settings`, {
        alpaca_api_key: apiKey,
        alpaca_secret_key: secretKey,
        risk_level: riskLevel,
        daily_budget: parseFloat(dailyBudget),
        max_positions_per_day: parseInt(maxPositionsPerDay),
        stop_loss_percent: parseFloat(stopLossPercent),
        take_profit_percent: parseFloat(takeProfitPercent),
        trailing_stop_percent: parseFloat(trailingStopPercent),
        auto_trading_enabled: autoTradingEnabled
      });
      
      toast.success('Settings saved successfully!');
      setIsConfigured(true);
      fetchSettings(); // Reload settings to show saved values
    } catch (error) {
      toast.error('Failed to save settings: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRiskLevel = async (newRiskLevel) => {
    try {
      await axios.post(`${API}/settings`, {
        risk_level: newRiskLevel
      });
      setRiskLevel(newRiskLevel);
      toast.success(`Risk level updated to ${newRiskLevel}`);
    } catch (error) {
      toast.error('Failed to update risk level');
    }
  };


  const handleGetAISignal = async (symbol) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ai/signal/${symbol}`);
      setAiSignals([response.data, ...aiSignals.slice(0, 4)]);
      toast.success(`AI analysis generated for ${symbol}`);
    } catch (error) {
      toast.error('Failed to get AI signal: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchWatchlist = async () => {
    try {
      const response = await axios.get(`${API}/watchlist`);
      setWatchlist(response.data);
    } catch (error) {
      console.error('Failed to fetch watchlist:', error);
    }
  };

  const fetchWatchlistSignals = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/watchlist/signals`);
      setWatchlistSignals(response.data);
      toast.success('Watchlist signals updated');
    } catch (error) {
      toast.error('Failed to fetch watchlist signals');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToWatchlist = async (symbol) => {
    try {
      await axios.post(`${API}/watchlist?symbol=${symbol.toUpperCase()}`);
      toast.success(`${symbol} added to watchlist`);
      fetchWatchlist();
    } catch (error) {
      toast.error('Failed to add to watchlist');
    }
  };

  const handleRemoveFromWatchlist = async (symbol) => {
    try {
      await axios.delete(`${API}/watchlist/${symbol}`);
      toast.success(`${symbol} removed from watchlist`);
      fetchWatchlist();
      setWatchlistSignals(watchlistSignals.filter(s => s.symbol !== symbol));
    } catch (error) {
      toast.error('Failed to remove from watchlist');
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/alerts?unread_only=true`);
      setAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };

  const fetchLoginHistory = async () => {
    if (!isAdmin) return;
    try {
      setLoginHistoryLoading(true);
      const params = { limit: 200 };
      if (loginHistoryFilter === 'success') params.success_only = true;
      if (loginHistoryFilter === 'failed') params.success_only = false;
      const response = await axios.get(`${API}/auth/login-history`, { params });
      setLoginHistory(response.data.events || []);
    } catch (error) {
      console.error('Failed to fetch login history:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required to view login history');
      }
    } finally {
      setLoginHistoryLoading(false);
    }
  };

  const fetchExitRules = async () => {
    try {
      const response = await axios.get(`${API}/exit-rules`);
      setExitRules(response.data);
    } catch (error) {
      console.error('Failed to fetch exit rules:', error);
    }
  };

  const checkExitRules = async () => {
    try {
      await axios.post(`${API}/exit-rules/check`);
    } catch (error) {
      console.error('Failed to check exit rules:', error);
    }
  };

  const handleCreateExitRule = async (symbol, entryPrice) => {
    try {
      await axios.post(`${API}/exit-rules`, {
        symbol: symbol.toUpperCase(),
        entry_price: entryPrice,
        stop_loss_percent: parseFloat(stopLossPercent),
        take_profit_percent: parseFloat(takeProfitPercent),
        max_hold_days: parseInt(maxHoldDays)
      });
      toast.success(`Exit rule created for ${symbol}`);
      fetchExitRules();
    } catch (error) {
      toast.error('Failed to create exit rule');
    }
  };

  const handleExecuteTrade = async () => {
    if (!tradeSymbol || !tradeQuantity) {
      toast.error('Please enter symbol and quantity');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/trading/execute`, {
        symbol: tradeSymbol.toUpperCase(),
        quantity: tradeQuantity,
        side: tradeSide,
        order_type: 'market'
      });
      
      toast.success(`${tradeSide.toUpperCase()} order submitted for ${tradeSymbol}`);
      
      // Create exit rule for buy orders
      if (tradeSide === 'buy') {
        const quote = await axios.get(`${API}/market/quote/${tradeSymbol.toUpperCase()}`);
        const entryPrice = quote.data.last_price;
        await handleCreateExitRule(tradeSymbol, entryPrice);
      }
      
      fetchDashboardData();
      fetchScannerResults();
      fetchScannerResults();
    } catch (error) {
      toast.error('Trade failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  };

  const formatPercent = (value) => {
    const num = parseFloat(value) * 100;
    return num.toFixed(2) + '%';
  };

  // Show login if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <Toaster position="top-right" />
        <Login onLoginSuccess={handleLoginSuccess} />
      </>
    );
  }

  if (!isConfigured) {
    return (
      <div className="min-h-screen grid-lines flex items-center justify-center p-6">
        <Toaster position="top-right" />
        <Card className="w-full max-w-2xl glass-panel border-2 border-neon-cyan" data-testid="setup-card">
          <CardHeader className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-neon-cyan/20 border border-neon-cyan flex items-center justify-center">
                <Activity className="h-6 w-6 text-neon-cyan" />
              </div>
              <div>
                <CardTitle className="text-3xl font-bold tracking-wider uppercase neon-glow-cyan">
                  PKAI Trading Setup
                </CardTitle>
                <CardDescription className="text-base mt-1 font-mono text-xs">
                  Configure Alpaca API credentials to initialize trading system
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 glass-panel border border-neon-cyan/30 rounded-lg">
              <div className="flex gap-3">
                <AlertCircle className="h-5 w-5 text-neon-cyan mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-neon-cyan mb-1 uppercase text-sm tracking-wider">Get Alpaca API Keys</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed font-mono text-xs">
                    1. Sign up at <a href="https://alpaca.markets" target="_blank" rel="noopener noreferrer" className="underline font-medium text-neon-cyan">alpaca.markets</a><br />
                    2. Generate API keys from dashboard<br />
                    3. Start with PAPER TRADING (virtual money) to test safely
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="api-key" className="text-xs font-mono uppercase tracking-widest text-neon-cyan">
                  Alpaca API Key
                </Label>
                <Input
                  id="api-key"
                  data-testid="api-key-input"
                  type="text"
                  placeholder="PK..."
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="h-11 font-mono bg-background/50 border-neon-cyan/30 focus:border-neon-cyan"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="secret-key" className="text-xs font-mono uppercase tracking-widest text-neon-cyan">
                  Alpaca Secret Key
                </Label>
                <Input
                  id="secret-key"
                  data-testid="secret-key-input"
                  type="password"
                  placeholder="Enter your secret key"
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  className="h-11 font-mono bg-background/50 border-neon-cyan/30 focus:border-neon-cyan"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="risk-level" className="text-xs font-mono uppercase tracking-widest text-neon-cyan">
                  Risk Level
                </Label>
                <Select value={riskLevel} onValueChange={setRiskLevel}>
                  <SelectTrigger id="risk-level" data-testid="risk-level-select" className="h-11 font-mono bg-background/50 border-neon-cyan/30">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="glass-panel border-neon-cyan/30">
                    <SelectItem value="low">LOW - Conservative</SelectItem>
                    <SelectItem value="medium">MEDIUM - Balanced</SelectItem>
                    <SelectItem value="high">HIGH - Aggressive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              data-testid="save-settings-button"
              onClick={handleSaveSettings}
              disabled={loading}
              className="w-full h-12 text-base font-bold uppercase tracking-wider bg-neon-cyan hover:bg-neon-cyan/90 text-background neon-border-cyan"
              size="lg"
            >
              {loading ? 'Saving...' : 'Save & Initialize'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid-lines">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="glass-panel border-b border-neon-cyan/30 sticky top-0 z-10 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-card border border-neon-cyan neon-border-cyan flex items-center justify-center">
                <Activity className="h-5 w-5 text-neon-cyan" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-wider uppercase neon-glow-cyan" data-testid="app-title">
                  PKAI TRADING
                </h1>
                {accountInfo && (
                  <div className="flex gap-2">
                    <Badge variant="outline" className="mt-1 border-neon-purple text-neon-purple font-mono text-xs" data-testid="trading-mode-badge">
                      {accountInfo.is_paper ? 'PAPER MODE' : 'LIVE MODE'}
                    </Badge>
                    <Badge variant="outline" className="mt-1 border-neon-cyan text-neon-cyan font-mono text-xs">
                      {settings?.strategy_preset ? 
                        (settings.strategy_preset === 'daytrader' ? 'DAY TRADER' : 
                         settings.strategy_preset === 'letitride' ? 'LET IT RIDE' : 
                         settings.strategy_preset.toUpperCase()) 
                        : 'CUSTOM'}
                    </Badge>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {automationStatus && automationStatus.active_today && (
                <Button
                  onClick={handleEmergencyStop}
                  disabled={loading}
                  variant="destructive"
                  size="sm"
                  className="font-bold uppercase tracking-wider neon-border-cyan bg-destructive hover:bg-destructive/90"
                  data-testid="emergency-stop-button"
                >
                  <AlertCircle className="h-4 w-4 mr-1" />
                  STOP
                </Button>
              )}
              {alerts.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveTab('alerts')}
                  className="relative border-neon-cyan/50 hover:border-neon-cyan"
                  data-testid="alerts-button"
                >
                  <Bell className="h-4 w-4" />
                  <Badge className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs bg-destructive animate-pulse-glow" variant="destructive">
                    {alerts.length}
                  </Badge>
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActiveTab('settings')}
                className="border-neon-cyan/50 hover:border-neon-cyan"
                data-testid="settings-button"
              >
                <SettingsIcon className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-destructive/50 hover:border-destructive text-destructive"
                data-testid="logout-button"
              >
                <Lock className="h-4 w-4 mr-1" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* COMBO PLACEMENT 2: Dashboard Dismissible Banner */}
      {!dashboardBannerDismissed && (
        <div className="bg-yellow-500/10 border-b-2 border-yellow-500/30">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-start gap-4">
              <AlertCircle className="h-6 w-6 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-yellow-500 mb-1">⚠️ SWING TRADING ONLY</h3>
                <p className="text-foreground/90 text-sm">
                  This bot is for active swing trading (5-30 days), NOT retirement accounts (401k, IRA) or long-term investing. Trade with risk capital only.
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDashboardBannerDismissed(true);
                  localStorage.setItem('dashboard-banner-dismissed', 'true');
                }}
                className="text-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/10"
              >
                ✕
              </Button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 glass-panel border border-neon-cyan/30" data-testid="main-tabs">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan font-mono uppercase text-xs" data-testid="dashboard-tab">Dashboard</TabsTrigger>
            <TabsTrigger value="scanresults" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan font-mono uppercase text-xs" data-testid="scanresults-tab">
              Scanner {scannerResults && <Badge className="ml-2 bg-neon-cyan text-background">{scannerResults.executed?.length || 0}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan font-mono uppercase text-xs" data-testid="analytics-tab">Analytics</TabsTrigger>
            <TabsTrigger value="trading" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan font-mono uppercase text-xs" data-testid="trading-tab">Trading</TabsTrigger>
            <TabsTrigger value="alerts" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan font-mono uppercase text-xs" data-testid="alerts-tab">
              Alerts {alerts.length > 0 && <Badge className="ml-2 bg-destructive animate-pulse-glow" variant="destructive">{alerts.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan font-mono uppercase text-xs" data-testid="history-tab">History</TabsTrigger>
            <TabsTrigger value="howitworks" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan font-mono uppercase text-xs" data-testid="howitworks-tab">How It Works</TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan font-mono uppercase text-xs" data-testid="settings-tab">Settings</TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="activity" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan font-mono uppercase text-xs" data-testid="activity-tab">Activity</TabsTrigger>
            )}
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Start Trading Today Button */}
            {automationStatus && !automationStatus.active_today && (
              <Card className="border-2 border-neon-cyan neon-border-cyan glass-panel" data-testid="start-trading-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-14 w-14 rounded-full bg-neon-cyan/20 border border-neon-cyan flex items-center justify-center">
                        <Zap className="h-7 w-7 text-neon-cyan" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-neon-cyan mb-1 uppercase tracking-wider">Ready to Trade?</h3>
                        <p className="text-sm text-muted-foreground font-mono">
                          AI SCAN • AUTO EXECUTE • ${automationStatus.daily_budget} BUDGET • MAX {automationStatus.max_positions_per_day} POS
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleStartTradingToday}
                      size="lg"
                      className="bg-neon-cyan hover:bg-neon-cyan/90 text-background h-14 px-8 text-lg font-bold uppercase tracking-wider neon-border-cyan"
                      data-testid="start-trading-today-button"
                    >
                      <Zap className="h-5 w-5 mr-2" />
                      Start Trading
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Trading Active Banner */}
            {automationStatus && automationStatus.active_today && (
              <div className="glass-panel border-2 border-success neon-border-cyan rounded-lg p-4" data-testid="automation-banner">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-success/20 border border-success flex items-center justify-center">
                      <Zap className="h-5 w-5 text-success animate-pulse" />
                    </div>
                    <div>
                      <h3 className="font-bold text-success text-lg uppercase tracking-wider">Trading Active</h3>
                      <p className="text-sm text-muted-foreground font-mono">
                        {automationStatus.positions_today}/{automationStatus.max_positions_per_day} positions • 
                        ${automationStatus.daily_budget} budget • 
                        {automationStatus.stop_loss_percent}% stop / {automationStatus.take_profit_percent}% profit
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleRunScanner}
                      disabled={loading}
                      className="bg-neon-cyan hover:bg-neon-cyan/90 text-background font-mono uppercase text-xs tracking-wider"
                      data-testid="run-scanner-button"
                    >
                      <Zap className="h-4 w-4 mr-2" />
                      Run Scanner
                    </Button>
                    <Button
                      onClick={handleRunAutomationNow}
                      disabled={loading}
                      variant="outline"
                      className="border-neon-cyan/50 hover:border-neon-cyan font-mono uppercase text-xs"
                      data-testid="run-automation-button"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Run Now
                    </Button>
                    <Button
                      onClick={handleResetCounter}
                      variant="outline"
                      size="sm"
                      className="border-warning/50 text-warning hover:bg-warning/10 font-mono uppercase text-xs"
                      title="Reset position counter to 0/10"
                    >
                      Reset
                    </Button>
                    <Button
                      onClick={handleStopTradingToday}
                      variant="outline"
                      className="border-destructive/50 text-destructive hover:bg-destructive/10 font-mono uppercase text-xs"
                      data-testid="stop-trading-button"
                    >
                      Stop Today
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Active Strategy Card - Prominent Display */}
            <Card className="glass-panel border-2 border-neon-purple/50 bg-neon-purple/5">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-neon-purple text-lg">
                  <Target className="h-5 w-5" />
                  ACTIVE STRATEGY
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">
                      {settings?.strategy_preset ? 
                        (settings.strategy_preset === 'daytrader' ? 'Day Trader' : 
                         settings.strategy_preset === 'letitride' ? 'Let It Ride' : 
                         settings.strategy_preset.charAt(0).toUpperCase() + settings.strategy_preset.slice(1)) 
                        : 'Custom Settings'}
                    </h3>
                    <div className="flex gap-4 text-sm text-muted-foreground">
                      <span>Stop: <strong className="text-foreground">{settings?.stop_loss_percent || 5}%</strong></span>
                      <span>•</span>
                      <span>Hold: <strong className="text-foreground">{settings?.min_hold_hours || 0}hrs</strong></span>
                      <span>•</span>
                      <span>Max: <strong className="text-foreground">{settings?.max_positions_per_day || 10} pos</strong></span>
                    </div>
                  </div>
                  <Button
                    onClick={() => setActiveTab('settings')}
                    variant="outline"
                    className="border-neon-purple text-neon-purple hover:bg-neon-purple/10"
                    size="sm"
                  >
                    Change
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Portfolio Overview */}
            {accountInfo && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="glass-panel border border-neon-cyan/30" data-testid="portfolio-value-card">
                  <CardHeader className="pb-3">
                    <CardDescription className="text-xs uppercase tracking-widest font-mono text-neon-cyan">
                      Portfolio Value
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-bold tracking-tight font-mono neon-glow-cyan">
                      {formatCurrency(accountInfo.portfolio_value)}
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-panel border border-neon-purple/30" data-testid="buying-power-card">
                  <CardHeader className="pb-3">
                    <CardDescription className="text-xs uppercase tracking-widest font-mono text-neon-purple">
                      Buying Power
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-bold tracking-tight font-mono neon-glow-purple">
                      {formatCurrency(accountInfo.buying_power)}
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-panel border border-success/30" data-testid="cash-card">
                  <CardHeader className="pb-3">
                    <CardDescription className="text-xs uppercase tracking-widest font-mono text-success">
                      Cash
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-bold tracking-tight font-mono text-success">
                      {formatCurrency(accountInfo.cash)}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Main Grid */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              {/* AI Signals */}

              {/* Positions */}
              <Card className="md:col-span-7 glass-panel border border-neon-cyan/30" data-testid="positions-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl font-bold uppercase tracking-wider text-neon-cyan">
                        <BarChart3 className="h-5 w-5 inline mr-2" />
                        Active Positions
                      </CardTitle>
                      <CardDescription className="text-muted-foreground font-mono text-xs mt-1">Real-time holdings & performance</CardDescription>
                    </div>
                    <Button
                      onClick={syncPositions}
                      variant="outline"
                      size="sm"
                      disabled={loading}
                      className="border-neon-cyan/50 hover:border-neon-cyan font-mono uppercase text-xs"
                      data-testid="sync-positions-button"
                    >
                      Sync
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {positions.map((position, idx) => {
                      const isProfit = parseFloat(position.unrealized_pl) >= 0;
                      return (
                        <div
                          key={position.symbol}
                          className="p-4 border border-neon-cyan/30 rounded-lg glass-panel hover:border-neon-cyan transition-all"
                          data-testid={`position-${idx}`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <span className="font-bold font-mono text-xl text-neon-cyan">{position.symbol}</span>
                              <span className="text-sm text-muted-foreground font-mono">{position.quantity} shares</span>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-lg font-mono">{formatCurrency(position.market_value)}</div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground font-mono text-xs uppercase">Entry:</span>
                              <span className="ml-2 font-mono font-medium">{formatCurrency(position.avg_entry_price)}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground font-mono text-xs uppercase">Current:</span>
                              <span className="ml-2 font-mono font-medium">{formatCurrency(position.current_price)}</span>
                            </div>
                          </div>
                          <div className="mt-3 flex items-center justify-between pt-3 border-t border-neon-cyan/20">
                            <div className={`flex items-center gap-2 text-sm font-semibold ${isProfit ? 'text-success' : 'text-destructive'}`}>
                              {isProfit ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                              <span className="font-mono">{formatCurrency(position.unrealized_pl)}</span>
                            </div>
                            <span className={`text-sm font-semibold font-mono ${isProfit ? 'text-success' : 'text-destructive'}`}>
                              {formatPercent(position.unrealized_plpc)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    {positions.length === 0 && (
                      <div className="text-center py-12 text-muted-foreground">
                        <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p className="font-mono text-xs uppercase">No active positions</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Pending Orders Section */}
              <Card className="md:col-span-12 border-2 border-yellow-400 shadow-lg bg-gradient-to-br from-yellow-50 to-orange-50">
                <CardHeader className="bg-yellow-100/50">
                  <CardTitle className="text-xl font-bold text-yellow-800 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Clock className="h-5 w-5" />
                    Pending Orders
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pendingOrders.map((order, idx) => (
                      <div key={idx} className="p-4 bg-white rounded-lg border-2 border-yellow-300 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-bold font-mono text-2xl text-slate-900">{order.symbol}</span>
                          <Badge className="bg-yellow-500 text-white text-xs font-bold px-3 py-1">
                            {order.status || 'PENDING'}
                          </Badge>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-600 font-medium">Quantity:</span>
                            <span className="font-mono font-bold text-slate-900">{order.qty} shares</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600 font-medium">Type:</span>
                            <span className="font-mono text-slate-900">{order.side} {order.type}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600 font-medium">Price:</span>
                            <span className="font-mono font-bold text-slate-900">
                              {order.limit_price ? `$${order.limit_price}` : 'Market'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {pendingOrders.length === 0 && (
                      <div className="col-span-full text-center py-8 text-slate-500">
                        <Clock className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p className="font-mono text-sm">No pending orders</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Watchlist Tab */}

          {/* Scanner Results Tab */}
          {/* Scanner Results Tab */}
          <TabsContent value="scanresults" className="space-y-6">
            
            {/* Date & Filter Controls */}
            {allScanResults && allScanResults.length > 0 && (
              <Card className="glass-panel border border-neon-cyan/30">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label className="text-neon-cyan font-mono text-xs uppercase">Select Scan</Label>
                      <Select value={String(selectedScanIndex)} onValueChange={(v) => {
                        setSelectedScanIndex(parseInt(v));
                        setScannerResults(allScanResults[parseInt(v)]);
                      }}>
                        <SelectTrigger className="bg-background/50 border-neon-cyan/30 text-foreground">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="glass-panel border-neon-cyan/30">
                          {allScanResults.map((scan, idx) => (
                            <SelectItem key={idx} value={String(idx)}>
                              {new Date(scan.timestamp).toLocaleString()}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-neon-cyan font-mono text-xs uppercase">Filter by Status</Label>
                      <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="bg-background/50 border-neon-cyan/30 text-foreground">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="glass-panel border-neon-cyan/30">
                          <SelectItem value="all">All Stocks</SelectItem>
                          <SelectItem value="executed">Executed (Bought)</SelectItem>
                          <SelectItem value="passed_filters">Passed Filters</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-neon-cyan font-mono text-xs uppercase">Rejection Reason</Label>
                      <Select value={filterReason} onValueChange={setFilterReason}>
                        <SelectTrigger className="bg-background/50 border-neon-cyan/30 text-foreground">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="glass-panel border-neon-cyan/30">
                          <SelectItem value="all">All Reasons</SelectItem>
                          <SelectItem value="price_too_low">Price Too Low</SelectItem>
                          <SelectItem value="price_too_high">Price Too High</SelectItem>
                          <SelectItem value="volume_too_low">Volume Too Low</SelectItem>
                          <SelectItem value="atr_too_low">ATR Too Low</SelectItem>
                          <SelectItem value="momentum_too_low">Momentum Too Low</SelectItem>
                          <SelectItem value="momentum_too_high">Momentum Too High</SelectItem>
                          <SelectItem value="no_setup">No Setup Pattern</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-neon-cyan font-mono text-xs uppercase">Search Symbol</Label>
                      <Input
                        placeholder="AAPL, TSLA..."
                        value={searchSymbol}
                        onChange={(e) => setSearchSymbol(e.target.value.toUpperCase())}
                        className="bg-background/50 border-neon-cyan/30 text-foreground"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {scannerResults && scannerResults.all_scanned && (
              <Card className="glass-panel border border-neon-cyan/30">
                <CardHeader>
                  <CardTitle className="text-neon-cyan uppercase tracking-wider">All Scanned Stocks ({
                    scannerResults.all_scanned
                      .filter(s => filterStatus === 'all' || s.status === filterStatus)
                      .filter(s => filterReason === 'all' || s.reason === filterReason)
                      .filter(s => !searchSymbol || s.symbol.includes(searchSymbol))
                      .length
                  })</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 glass-panel border-b-2 border-neon-cyan/30">
                        <tr>
                          <th className="text-left p-2 text-neon-cyan font-mono uppercase text-xs">Symbol</th>
                          <th className="text-left p-2 text-neon-cyan font-mono uppercase text-xs">Status</th>
                          <th className="text-left p-2 text-neon-cyan font-mono uppercase text-xs">Reason</th>
                          <th className="text-right p-2 text-neon-cyan font-mono uppercase text-xs">Price</th>
                          <th className="text-right p-2 text-neon-cyan font-mono uppercase text-xs">Momentum</th>
                          <th className="text-right p-2 text-neon-cyan font-mono uppercase text-xs">Score</th>
                          <th className="text-right p-2 text-neon-cyan font-mono uppercase text-xs">Position $</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scannerResults.all_scanned
                          .filter(s => filterStatus === 'all' || s.status === filterStatus)
                          .filter(s => filterReason === 'all' || s.reason === filterReason)
                          .filter(s => !searchSymbol || s.symbol.includes(searchSymbol))
                          .slice(0, 100)
                          .map((stock, idx) => (
                            <tr key={idx} className="border-b border-neon-cyan/10 hover:bg-neon-cyan/5">
                              <td className="p-2 font-mono font-bold text-neon-cyan">{stock.symbol}</td>
                              <td className="p-2">
                                {stock.status === 'executed' && <Badge className="bg-success text-background">BOUGHT</Badge>}
                                {stock.status === 'passed_filters' && <Badge className="bg-neon-cyan text-background">PASSED</Badge>}
                                {stock.status === 'rejected' && <Badge variant="outline" className="border-destructive text-destructive">REJECTED</Badge>}
                              </td>
                              <td className="p-2 text-xs text-muted-foreground">{stock.reason_text}</td>
                              <td className="p-2 text-right text-foreground">${stock.price?.toFixed(2) || 'N/A'}</td>
                              <td className={`p-2 text-right ${stock.momentum_5d > 0 ? 'text-success' : 'text-destructive'}`}>
                                {stock.momentum_5d?.toFixed(1)}%
                              </td>
                              <td className="p-2 text-right">
                                {stock.setup_score ? (
                                  <span className={`font-bold font-mono ${
                                    stock.setup_score >= 80 ? 'text-success' : 
                                    stock.setup_score >= 70 ? 'text-neon-cyan' : 
                                    'text-foreground'
                                  }`}>
                                    {stock.setup_score}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                              <td className="p-2 text-right">
                                {stock.conviction ? (
                                  <span className={`font-bold font-mono ${
                                    stock.conviction >= 9 ? 'text-success' : 
                                    stock.conviction >= 7 ? 'text-neon-cyan' : 
                                    'text-foreground'
                                  }`}>
                                    ${(() => {
                                      const budget = automationStatus?.daily_budget || 5000;
                                      const maxPos = automationStatus?.max_positions_per_day || 10;
                                      const baseSize = budget / maxPos;
                                      let multiplier = 1.0;
                                      const conv = stock.conviction;
                                      if (conv >= 9) multiplier = 1.8 + (conv - 9) * 0.2;
                                      else if (conv >= 7) multiplier = 1.3 + (conv - 7) * 0.1;
                                      else if (conv >= 5) multiplier = 0.8 + (conv - 5) * 0.1;
                                      else if (conv >= 3) multiplier = 0.4 + (conv - 3) * 0.1;
                                      const posValue = Math.max(100, Math.min(budget * 0.25, baseSize * multiplier));
                                      return Math.round(posValue);
                                    })()}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {(!scannerResults || !scannerResults.all_scanned || scannerResults.all_scanned.length === 0) && (
              <Card className="glass-panel border border-neon-cyan/30">
                <CardContent className="py-12 text-center">
                  <p className="text-foreground">No scan data yet. Run a scan to see results!</p>
                  {scannerResults && !scannerResults.all_scanned && (
                    <p className="text-sm text-muted-foreground mt-2">
                      (Old scan format - run a new scan for full transparency)
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Analytics Tab - 4 COMPREHENSIVE VIEWS */}
          <TabsContent value="analytics" className="space-y-6">
            <Tabs defaultValue="overview">
              <TabsList className="mb-4 glass-panel border border-neon-cyan/30">
                <TabsTrigger value="overview" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan">Overview</TabsTrigger>
                <TabsTrigger value="portfolio" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan">Portfolio</TabsTrigger>
                <TabsTrigger value="per-stock" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan">Per-Stock</TabsTrigger>
                <TabsTrigger value="ai" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan">AI Analysis</TabsTrigger>
              </TabsList>

              {/* VIEW 1: Overview Dashboard */}
              <TabsContent value="overview" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                      Performance Overview
                    </CardTitle>
                    <CardDescription>Key metrics at a glance</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {analyticsOverview && analyticsOverview.total_trades > 0 ? (
                      <div className="space-y-6">
                        {/* Key Metrics Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="bg-slate-50 p-4 rounded-lg">
                            <p className="text-sm text-slate-600 mb-1">Total P&L</p>
                            <p className={`text-2xl font-bold ${analyticsOverview.total_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ${analyticsOverview.total_pnl >= 0 ? '+' : ''}{analyticsOverview.total_pnl}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">{analyticsOverview.total_pnl_percent}% ROI</p>
                          </div>
                          
                          <div className="bg-slate-50 p-4 rounded-lg">
                            <p className="text-sm text-slate-600 mb-1">Win Rate</p>
                            <p className="text-2xl font-bold text-green-600">
                              {analyticsOverview.win_rate}%
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {analyticsOverview.wins}W / {analyticsOverview.losses}L
                            </p>
                          </div>
                          
                          <div className="bg-slate-50 p-4 rounded-lg">
                            <p className="text-sm text-slate-600 mb-1">Avg Winner</p>
                            <p className="text-2xl font-bold text-green-600">
                              +{analyticsOverview.avg_winner}%
                            </p>
                            <p className="text-xs text-slate-500 mt-1">${analyticsOverview.avg_winner_dollars}</p>
                          </div>
                          
                          <div className="bg-slate-50 p-4 rounded-lg">
                            <p className="text-sm text-slate-600 mb-1">Avg Loser</p>
                            <p className="text-2xl font-bold text-red-600">
                              -{analyticsOverview.avg_loser}%
                            </p>
                            <p className="text-xs text-slate-500 mt-1">-${analyticsOverview.avg_loser_dollars}</p>
                          </div>
                        </div>

                        {/* Additional Stats */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="bg-slate-800/50 border border-neon-cyan/20 p-4 rounded-lg">
                            <p className="text-sm text-muted-foreground mb-1">Open Positions</p>
                            <p className="text-xl font-bold text-foreground">{analyticsOverview.open_positions}</p>
                            <p className="text-xs text-muted-foreground">${analyticsOverview.open_positions_value}</p>
                          </div>
                          <div className="bg-slate-800/50 border border-neon-cyan/20 p-4 rounded-lg">
                            <p className="text-sm text-muted-foreground mb-1">Total Trades</p>
                            <p className="text-xl font-bold text-foreground">{analyticsOverview.total_trades}</p>
                            <p className="text-xs text-muted-foreground">Completed</p>
                          </div>
                          <div className="bg-slate-800/50 border border-neon-cyan/20 p-4 rounded-lg">
                            <p className="text-sm text-muted-foreground mb-1">Capital Deployed</p>
                            <p className="text-xl font-bold text-foreground">${analyticsOverview.total_capital_deployed.toFixed(0)}</p>
                            <p className="text-xs text-muted-foreground">Total invested</p>
                          </div>
                        </div>

                        {/* Win/Loss Ratio Visual */}
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <h4 className="font-semibold mb-3">Win/Loss Distribution</h4>
                          <div className="flex gap-2 h-10 rounded overflow-hidden">
                            <div 
                              className="bg-green-500 flex items-center justify-center text-white text-sm font-semibold"
                              style={{width: `${analyticsOverview.win_rate}%`}}
                            >
                              {analyticsOverview.wins} Wins
                            </div>
                            <div 
                              className="bg-red-500 flex items-center justify-center text-white text-sm font-semibold"
                              style={{width: `${100 - analyticsOverview.win_rate}%`}}
                            >
                              {analyticsOverview.losses} Losses
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <p>No completed trades yet. Execute some trades to see your performance.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* VIEW 2: Portfolio Timeline */}
              <TabsContent value="portfolio" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5 text-blue-600" />
                      Portfolio Timeline
                    </CardTitle>
                    <CardDescription>Cumulative P&L over time</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {analyticsPortfolio && analyticsPortfolio.timeline && analyticsPortfolio.timeline.length > 0 ? (
                      <div className="space-y-6">
                        {/* Summary Stats */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="bg-slate-800/50 border border-neon-cyan/20 p-4 rounded-lg">
                            <p className="text-sm text-muted-foreground mb-1">Total Return</p>
                            <p className={`text-2xl font-bold ${analyticsPortfolio.summary.total_return >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              ${analyticsPortfolio.summary.total_return >= 0 ? '+' : ''}{analyticsPortfolio.summary.total_return}
                            </p>
                          </div>
                          <div className="bg-slate-800/50 border border-neon-cyan/20 p-4 rounded-lg">
                            <p className="text-sm text-muted-foreground mb-1">Completed Trades</p>
                            <p className="text-2xl font-bold text-foreground">{analyticsPortfolio.summary.num_trades}</p>
                          </div>
                          <div className="bg-slate-800/50 border border-neon-cyan/20 p-4 rounded-lg">
                            <p className="text-sm text-muted-foreground mb-1">Period</p>
                            <p className="text-xs text-foreground">
                              {new Date(analyticsPortfolio.summary.start_date).toLocaleDateString()} - {new Date(analyticsPortfolio.summary.end_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        {/* Trade Timeline */}
                        <div className="space-y-2">
                          <h4 className="font-semibold mb-3">Trade History</h4>
                          <div className="max-h-96 overflow-y-auto space-y-2">
                            {analyticsPortfolio.timeline.map((item, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-slate-50 p-3 rounded">
                                <div className="flex items-center gap-3">
                                  <div className={`w-2 h-2 rounded-full ${item.pnl >= 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                  <div>
                                    <p className="font-semibold">{item.symbol}</p>
                                    <p className="text-xs text-slate-500">{new Date(item.date).toLocaleString()}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className={`font-bold ${item.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    ${item.pnl >= 0 ? '+' : ''}{item.pnl}
                                  </p>
                                  <p className="text-xs text-slate-500">Cum: ${item.cumulative_pnl}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <p>No completed trades yet. Portfolio timeline will appear after exits.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* VIEW 3: Per-Stock Performance */}
              <TabsContent value="per-stock" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-purple-600" />
                      Per-Stock Breakdown
                    </CardTitle>
                    <CardDescription>Individual stock performance</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {analyticsPerStock && analyticsPerStock.stocks && analyticsPerStock.stocks.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-2 font-semibold">Symbol</th>
                              <th className="text-right p-2 font-semibold">Invested</th>
                              <th className="text-right p-2 font-semibold">Returned</th>
                              <th className="text-right p-2 font-semibold">P&L</th>
                              <th className="text-right p-2 font-semibold">ROI</th>
                              <th className="text-right p-2 font-semibold">Trades</th>
                              <th className="text-right p-2 font-semibold">Win Rate</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analyticsPerStock.stocks.map((stock) => (
                              <tr key={stock.symbol} className="border-b hover:bg-slate-50">
                                <td className="p-2 font-semibold">{stock.symbol}</td>
                                <td className="text-right p-2">${stock.total_invested.toFixed(2)}</td>
                                <td className="text-right p-2">${stock.total_returned.toFixed(2)}</td>
                                <td className={`text-right p-2 font-bold ${stock.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  ${stock.pnl >= 0 ? '+' : ''}{stock.pnl.toFixed(2)}
                                </td>
                                <td className={`text-right p-2 font-bold ${stock.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {stock.roi >= 0 ? '+' : ''}{stock.roi.toFixed(1)}%
                                </td>
                                <td className="text-right p-2">{stock.num_trades}</td>
                                <td className="text-right p-2">{stock.win_rate}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <p>No stock data available. Start trading to see per-stock breakdown.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* VIEW 4: AI Performance */}
              <TabsContent value="ai" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="h-5 w-5 text-yellow-600" />
                      AI Performance Rating
                    </CardTitle>
                    <CardDescription>AI score correlation with actual returns</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {analyticsAI && analyticsAI.score_performance && analyticsAI.score_performance.length > 0 ? (
                      <div className="space-y-6">
                        {/* Summary */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-slate-800/50 border border-neon-cyan/20 p-4 rounded-lg">
                            <p className="text-sm text-muted-foreground mb-1">Total AI Analyzed</p>
                            <p className="text-2xl font-bold text-foreground">{analyticsAI.summary.total_ai_analyzed}</p>
                          </div>
                          <div className="bg-slate-800/50 border border-neon-cyan/20 p-4 rounded-lg">
                            <p className="text-sm text-muted-foreground mb-1">Stocks Traded</p>
                            <p className="text-2xl font-bold text-foreground">{analyticsAI.summary.total_traded}</p>
                          </div>
                        </div>

                        {/* Score Performance Table */}
                        <div>
                          <h4 className="font-semibold mb-3">Performance by AI Score Range</h4>
                          <div className="space-y-3">
                            {analyticsAI.score_performance.map((bucket) => (
                              <div key={bucket.score_range} className="bg-slate-50 p-4 rounded-lg">
                                <div className="flex justify-between items-center mb-2">
                                  <div>
                                    <p className="font-bold text-lg">Score: {bucket.score_range}</p>
                                    <p className="text-sm text-slate-600">{bucket.count} stocks analyzed</p>
                                  </div>
                                  <div className="text-right">
                                    <p className={`text-2xl font-bold ${bucket.avg_return >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {bucket.avg_return >= 0 ? '+' : ''}{bucket.avg_return}%
                                    </p>
                                    <p className="text-sm text-slate-600">Avg Return</p>
                                  </div>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <div>
                                    <span className="text-green-600 font-semibold">{bucket.wins}W</span>
                                    {' / '}
                                    <span className="text-red-600 font-semibold">{bucket.losses}L</span>
                                  </div>
                                  <div className="font-semibold">Win Rate: {bucket.win_rate}%</div>
                                </div>
                                <div className="mt-2 flex gap-1 h-2 rounded overflow-hidden">
                                  <div className="bg-green-500" style={{width: `${bucket.win_rate}%`}}></div>
                                  <div className="bg-red-500" style={{width: `${100 - bucket.win_rate}%`}}></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <p>Not enough AI analysis data yet. Run scanner to collect AI performance metrics.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Trading Tab */}
          <TabsContent value="trading" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-slate-200 shadow-sm" data-testid="manual-trade-card">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                    <TrendingUp className="h-5 w-5 inline mr-2" />
                    Manual Trade Execution
                  </CardTitle>
                  <CardDescription>Execute trades directly outside of automation</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold uppercase tracking-wide">Stock Symbol</Label>
                      <Input
                        placeholder="AAPL"
                        value={tradeSymbol}
                        onChange={(e) => setTradeSymbol(e.target.value.toUpperCase())}
                        className="text-lg font-mono"
                        data-testid="symbol-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-semibold uppercase tracking-wide">Quantity</Label>
                      <Input
                        type="number"
                        placeholder="100"
                        value={tradeQuantity}
                        onChange={(e) => setTradeQuantity(e.target.value)}
                        className="text-lg"
                        data-testid="quantity-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-semibold uppercase tracking-wide">Side</Label>
                      <div className="flex gap-2">
                        <Button
                          variant={tradeSide === 'buy' ? 'default' : 'outline'}
                          onClick={() => setTradeSide('buy')}
                          className="flex-1 h-12"
                          data-testid="buy-button"
                        >
                          <TrendingUp className="h-4 w-4 mr-2" />
                          Buy
                        </Button>
                        <Button
                          variant={tradeSide === 'sell' ? 'destructive' : 'outline'}
                          onClick={() => setTradeSide('sell')}
                          className="flex-1 h-12"
                          data-testid="sell-button"
                        >
                          <TrendingDown className="h-4 w-4 mr-2" />
                          Sell
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-200">
                    <Button
                      onClick={handleExecuteTrade}
                      disabled={loading}
                      className="w-full h-12 text-base font-medium"
                      size="lg"
                      data-testid="execute-trade-button"
                    >
                      {loading ? 'Executing...' : `Execute ${tradeSide === 'buy' ? 'Buy' : 'Sell'} Order`}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-sm" data-testid="exit-rules-card">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                    <Target className="h-5 w-5 inline mr-2" />
                    Automated Exit Rules
                  </CardTitle>
                  <CardDescription>Set stop-loss and take-profit targets</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold uppercase tracking-wide">Stop Loss %</Label>
                      <Input
                        type="number"
                        value={stopLossPercent}
                        onChange={(e) => setStopLossPercent(e.target.value)}
                        placeholder="2"
                        data-testid="stop-loss-input"
                      />
                      <p className="text-xs text-slate-500">Exit if down {stopLossPercent}%</p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-semibold uppercase tracking-wide">Take Profit %</Label>
                      <Input
                        type="number"
                        value={takeProfitPercent}
                        onChange={(e) => setTakeProfitPercent(e.target.value)}
                        placeholder="5"
                        data-testid="take-profit-input"
                      />
                      <p className="text-xs text-slate-500">Exit if up {takeProfitPercent}%</p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-semibold uppercase tracking-wide">Max Hold Days</Label>
                      <Input
                        type="number"
                        value={maxHoldDays}
                        onChange={(e) => setMaxHoldDays(e.target.value)}
                        placeholder="7"
                        data-testid="max-hold-days-input"
                      />
                      <p className="text-xs text-slate-500">Auto-alert after {maxHoldDays} days</p>
                    </div>
                  </div>

                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <CheckCircle className="h-4 w-4 inline mr-1" />
                      Exit rules automatically created for all buy orders
                    </p>
                  </div>

                  {exitRules.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm">Active Rules:</h4>
                      {exitRules.slice(0, 3).map((rule, idx) => (
                        <div key={rule.id} className="text-sm p-2 bg-slate-50 rounded" data-testid={`exit-rule-${idx}`}>
                          <span className="font-mono font-bold">{rule.symbol}</span>
                          <span className="text-slate-500 ml-2">
                            📉 -{rule.stop_loss_percent}% | 📈 +{rule.take_profit_percent}% | ⏰ {rule.max_hold_days}d
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts">
            <Card className="glass-panel border border-neon-cyan/30" data-testid="alerts-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl font-bold uppercase tracking-wider text-neon-cyan">
                      <Bell className="h-5 w-5 inline mr-2" />
                      Trading Alerts
                    </CardTitle>
                    <CardDescription className="text-muted-foreground font-mono text-sm">Exit signals and automated triggers</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => checkExitRules()}
                      variant="outline"
                      className="border-neon-cyan/50 hover:border-neon-cyan"
                      data-testid="check-alerts-button"
                    >
                      Check Now
                    </Button>
                    {alerts.length > 0 && (
                      <Button
                        onClick={async () => {
                          try {
                            await fetch(`${API}/alerts`, { 
                              method: 'DELETE',
                              headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
                            });
                            await fetchAlerts();
                            toast.success('Alerts cleared');
                          } catch (error) {
                            toast.error('Failed to clear alerts');
                          }
                        }}
                        variant="outline"
                        className="border-destructive/50 hover:border-destructive text-destructive"
                        data-testid="clear-alerts-button"
                      >
                        Clear All
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {alerts.map((alert, idx) => (
                    <div
                      key={alert.id}
                      className={`p-4 border rounded-lg glass-panel ${
                        alert.priority === 'critical' ? 'border-destructive bg-destructive/10' :
                        alert.priority === 'high' ? 'border-warning bg-warning/10' :
                        alert.priority === 'medium' ? 'border-neon-purple bg-neon-purple/10' :
                        'border-neon-cyan/30 bg-neon-cyan/5'
                      }`}
                      data-testid={`alert-${idx}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant={alert.priority === 'high' || alert.priority === 'critical' ? 'destructive' : 'secondary'} className="font-mono text-xs">
                              {alert.alert_type.replace('_', ' ').toUpperCase()}
                            </Badge>
                            <span className="text-xs text-muted-foreground font-mono">
                              {new Date(alert.created_at).toLocaleString('en-US', { 
                                month: 'short', 
                                day: 'numeric', 
                                hour: '2-digit', 
                                minute: '2-digit',
                                hour12: true 
                              })}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-foreground">{alert.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {alerts.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <Bell className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p className="font-mono text-sm">No alerts. Your positions are within targets.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card className="border-slate-200 shadow-sm" data-testid="history-card">
              <CardHeader>
                <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  Trade History
                </CardTitle>
                <CardDescription>Your recent trading activity</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {tradeHistory.map((trade, idx) => (
                    <div
                      key={trade.id}
                      className="p-4 border border-neon-cyan/20 rounded-lg bg-slate-800/50 flex items-center justify-between"
                      data-testid={`trade-history-${idx}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                          trade.side === 'buy' ? 'bg-green-900/30 border border-green-500/30' : 'bg-red-900/30 border border-red-500/30'
                        }`}>
                          {trade.side === 'buy' ? 
                            <TrendingUp className="h-5 w-5 text-green-400" /> : 
                            <TrendingDown className="h-5 w-5 text-red-400" />
                          }
                        </div>
                        <div>
                          <div className="font-bold font-mono text-lg text-foreground">{trade.symbol}</div>
                          <div className="text-sm text-muted-foreground">
                            {trade.quantity} shares @ {formatCurrency(trade.price)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant={trade.status === 'filled' ? 'default' : 'secondary'}>
                          {trade.status}
                        </Badge>
                        <div className="text-xs text-muted-foreground mt-1">
                          {new Date(trade.executed_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                  {tradeHistory.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <Activity className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p>No trade history yet.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-slate-200 shadow-sm" data-testid="settings-card">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                    Trading Settings
                  </CardTitle>
                  <CardDescription>Configure your trading preferences</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="api-key" className="text-sm font-semibold uppercase tracking-wide">
                        Alpaca API Key
                      </Label>
                      <Input
                        id="api-key"
                        type="text"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder="PKXXXXXXXXXXXXXXXXXX"
                        className="font-mono"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="secret-key" className="text-sm font-semibold uppercase tracking-wide">
                        Alpaca Secret Key
                      </Label>
                      <Input
                        id="secret-key"
                        type="password"
                        value={secretKey}
                        onChange={(e) => setSecretKey(e.target.value)}
                        placeholder="Enter secret key"
                        className="font-mono"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="settings-risk" className="text-sm font-semibold uppercase tracking-wide">
                        Risk Level
                      </Label>
                      <Select value={riskLevel} onValueChange={handleUpdateRiskLevel}>
                        <SelectTrigger id="settings-risk" data-testid="settings-risk-select" className="h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low - Conservative, stable stocks</SelectItem>
                          <SelectItem value="medium">Medium - Balanced approach</SelectItem>
                          <SelectItem value="high">High - Aggressive, high-growth</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <h4 className="font-semibold mb-2">Current Configuration</h4>
                      <div className="space-y-1 text-sm text-slate-600">
                        <div>Mode: <strong>{accountInfo?.is_paper ? 'Paper Trading' : 'Live Trading'}</strong></div>
                        <div>Risk Level: <strong>{riskLevel.toUpperCase()}</strong></div>
                        <div>AI Powered: <strong>Enabled (GPT-5.2)</strong></div>
                      </div>
                    </div>

                    <Button
                      onClick={handleSaveSettings}
                      className="w-full h-12"
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Save All Settings'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Theme Selector Card */}
              <Card className="glass-panel border border-neon-cyan/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-neon-cyan text-2xl">
                    <Sparkles className="h-6 w-6" />
                    Theme Selector
                  </CardTitle>
                  <CardDescription>Choose your visual style</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(themes).map(([key, theme]) => (
                    <div
                      key={key}
                      onClick={() => applyTheme(key)}
                      className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${
                        currentTheme === key
                          ? 'border-neon-cyan bg-neon-cyan/10'
                          : 'border-slate-700 bg-background/50 hover:border-neon-cyan/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-bold text-lg text-foreground">{theme.name}</h4>
                          <p className="text-sm text-muted-foreground">{theme.description}</p>
                        </div>
                        <div className="flex gap-2">
                          <div
                            className="w-8 h-8 rounded-full border-2 border-white/20"
                            style={{ backgroundColor: theme.primary }}
                          />
                          <div
                            className="w-8 h-8 rounded-full border-2 border-white/20"
                            style={{ backgroundColor: theme.background }}
                          />
                          <div
                            className="w-8 h-8 rounded-full border-2 border-white/20"
                            style={{ backgroundColor: theme.accent }}
                          />
                        </div>
                      </div>
                      {currentTheme === key && (
                        <div className="mt-2 flex items-center gap-1 text-neon-cyan text-sm font-semibold">
                          <CheckCircle className="h-4 w-4" />
                          Active
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Strategy Preset Selector Card */}
              <Card className="glass-panel border border-neon-purple/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-neon-purple text-2xl">
                    <Target className="h-6 w-6" />
                    Strategy Presets
                  </CardTitle>
                  <CardDescription>Select your trading style - all risk settings configured automatically</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    {
                      id: 'conservative',
                      name: 'Conservative Swing',
                      icon: '🛡️',
                      stop: '12%',
                      hold: '72hr',
                      desc: 'Safest approach with wide stops',
                      risk: 'Low',
                      color: 'green'
                    },
                    {
                      id: 'balanced',
                      name: 'Balanced Swing',
                      icon: '⚖️',
                      stop: '10%',
                      hold: '48hr',
                      desc: 'Recommended default for most traders',
                      risk: 'Medium',
                      color: 'blue',
                      recommended: true
                    },
                    {
                      id: 'aggressive',
                      name: 'Aggressive Momentum',
                      icon: '⚡',
                      stop: '8%',
                      hold: '24hr',
                      desc: 'Active trading with tighter stops',
                      risk: 'Medium-High',
                      color: 'orange'
                    },
                    {
                      id: 'daytrader',
                      name: 'Day Trader',
                      icon: '🔥',
                      stop: '3%',
                      hold: '0hr',
                      desc: 'Very tight stops, high activity',
                      risk: 'High',
                      color: 'red'
                    },
                    {
                      id: 'letitride',
                      name: 'Let It Ride',
                      icon: '🎢',
                      stop: '15%',
                      hold: '120hr',
                      desc: 'Patient trend following, big targets',
                      risk: 'Very High',
                      color: 'purple'
                    }
                  ].map((strategy) => (
                    <div
                      key={strategy.id}
                      className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${
                        settings?.strategy_preset === strategy.id
                          ? 'border-neon-purple bg-neon-purple/10 ring-2 ring-neon-purple/50'
                          : 'border-slate-700 bg-background/50 hover:border-neon-purple/50 hover:bg-neon-purple/5'
                      }`}
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/settings`, { strategy_preset: strategy.id });
                          await fetchSettings();
                          setAlertMessage(`✅ ${strategy.name} strategy activated!`);
                          setAlertType('success');
                          setShowAlert(true);
                          setTimeout(() => setShowAlert(false), 3000);
                        } catch (err) {
                          console.error('Failed to apply strategy:', err);
                          setAlertMessage('❌ Failed to apply strategy');
                          setAlertType('error');
                          setShowAlert(true);
                          setTimeout(() => setShowAlert(false), 3000);
                        }
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-2xl">{strategy.icon}</span>
                            <h4 className="font-bold text-lg text-foreground">{strategy.name}</h4>
                            {strategy.recommended && (
                              <span className="text-xs bg-neon-purple/20 text-neon-purple px-2 py-0.5 rounded">RECOMMENDED</span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{strategy.desc}</p>
                          <div className="flex gap-4 text-xs">
                            <span className="text-foreground">Stop: <strong>{strategy.stop}</strong></span>
                            <span className="text-foreground">Min Hold: <strong>{strategy.hold}</strong></span>
                            <span className="text-foreground">Risk: <strong className={`text-${strategy.color}-400`}>{strategy.risk}</strong></span>
                          </div>
                        </div>
                        {settings?.strategy_preset === strategy.id && (
                          <CheckCircle className="h-6 w-6 text-neon-purple flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  ))}
                  
                  <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-sm text-foreground">
                      <strong>💡 Tip:</strong> Start with <strong>Balanced Swing</strong> and run for 2 weeks. Review your Analytics tab, then adjust if needed. 
                      <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('howitworks'); }} className="text-neon-cyan underline ml-1">
                        Learn more about each strategy →
                      </a>
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-sm" data-testid="automation-settings-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                        <Zap className="h-5 w-5 inline mr-2" />
                        Automated Trading
                      </CardTitle>
                      <CardDescription>Let AI execute trades automatically</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{autoTradingEnabled ? 'ON' : 'OFF'}</span>
                      <Button
                        onClick={handleToggleAutomation}
                        variant={autoTradingEnabled ? 'default' : 'outline'}
                        size="sm"
                        data-testid="toggle-automation-button"
                      >
                        {autoTradingEnabled ? 'Disable' : 'Enable'}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm text-foreground">
                      <strong>💡 Risk Management:</strong> Stop-loss, take profit, and trailing stops are now controlled by your <strong>Strategy Preset</strong> (see above). Change your strategy to adjust all risk parameters at once.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold uppercase tracking-wide">Daily Budget ($)</Label>
                      <Input
                        type="number"
                        value={dailyBudget}
                        onChange={(e) => setDailyBudget(e.target.value)}
                        placeholder="1000"
                        disabled={!autoTradingEnabled}
                        data-testid="daily-budget-input"
                      />
                      <p className="text-xs text-slate-500">Max $ to invest per day</p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-semibold uppercase tracking-wide">Max Positions Per Day (Optional Override)</Label>
                      <Input
                        type="number"
                        value={maxPositionsPerDay}
                        onChange={(e) => setMaxPositionsPerDay(e.target.value)}
                        placeholder={settings?.max_positions_per_day || "7"}
                        disabled={!autoTradingEnabled}
                        data-testid="max-positions-input"
                      />
                      <p className="text-xs text-slate-500">Leave blank to use your strategy's default</p>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-200">
                    <Button
                      onClick={handleToggleAutomation}
                      className="w-full h-12"
                      variant={autoTradingEnabled ? 'destructive' : 'default'}
                      data-testid="save-automation-button"
                    >
                      {autoTradingEnabled ? 'Save & Keep Enabled' : 'Save & Enable Automation'}
                    </Button>
                  </div>

                  {autoTradingEnabled && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Zap className="h-5 w-5 text-green-600 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="font-semibold text-green-900 mb-1">Automation Active</h4>
                          <p className="text-sm text-green-800 leading-relaxed">
                            Bot will scan your watchlist every minute for AI BUY signals and auto-execute trades within your budget/position limits. Auto-exits when stop-loss or take-profit targets hit.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* How It Works Tab with Sub-Tabs */}
          <TabsContent value="howitworks" className="space-y-6">
            {/* PLACEMENT 1: Top Banner - Swing Trading Disclaimer */}
            <Card className="glass-panel border-2 border-yellow-500/50 bg-yellow-500/10">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <AlertCircle className="h-8 w-8 text-yellow-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-xl text-yellow-500 mb-2">⚠️ SWING TRADING ONLY</h3>
                    <p className="text-foreground text-base leading-relaxed">
                      This bot is designed for <strong className="text-yellow-500">active swing trading</strong> (5-30 day holds), NOT long-term investing, 401(k), retirement accounts, or buy-and-hold strategies. 
                      <strong className="text-yellow-500"> Trade with risk capital only.</strong> Past performance does not guarantee future results.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Tabs defaultValue="scanner">
              <TabsList className="mb-4 glass-panel border border-neon-cyan/30">
                <TabsTrigger value="scanner" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan">Scanner</TabsTrigger>
                <TabsTrigger value="position" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan">Position Sizing</TabsTrigger>
                <TabsTrigger value="exits" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan">Exit Strategy</TabsTrigger>
                <TabsTrigger value="transparency" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan">Transparency</TabsTrigger>
                <TabsTrigger value="faq" className="data-[state=active]:bg-neon-cyan/20 data-[state=active]:text-neon-cyan">FAQ</TabsTrigger>
              </TabsList>

              {/* Scanner Process Sub-Tab */}
              <TabsContent value="scanner" className="space-y-4">
                <Card className="glass-panel border border-neon-cyan/30">
                  <CardHeader>
                    <CardTitle className="text-neon-cyan uppercase tracking-wider text-2xl">3-Layer Scanning Process</CardTitle>
                    <CardDescription className="text-muted-foreground font-mono text-base">How the bot finds high-probability swing trades from 500 stocks</CardDescription>
                  </CardHeader>
                </Card>

                <Card className="glass-panel border border-neon-cyan/30">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-foreground text-xl">
                      <span className="bg-neon-cyan/20 text-neon-cyan border border-neon-cyan w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg">1</span>
                      Technical Pre-Screen
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="mb-3 text-foreground text-base">Scans <strong className="text-neon-cyan">225 most liquid stocks</strong> from major sectors</p>
                    <div className="bg-background/50 border border-neon-cyan/20 p-6 rounded-lg">
                      <ul className="space-y-2 text-base text-foreground">
                        <li>• Price: $10-$500 (avoids penny stocks & ultra-expensive)</li>
                        <li>• Volume: Min 3M shares/day (liquidity)</li>
                        <li>• ATR: Min 3% (volatility for swings)</li>
                        <li>• Momentum: +5% to +25% (strong trend)</li>
                        <li>• <strong className="text-neon-cyan">NEW:</strong> Earnings filter (skips stocks within 5 days of earnings)</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-panel border border-success/30">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-foreground text-xl">
                      <span className="bg-success/20 text-success border border-success w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg">2</span>
                      Catalyst Detection
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="mb-3 text-foreground text-base">Identifies stocks with potential catalysts</p>
                    <div className="bg-background/50 border border-success/20 p-6 rounded-lg">
                      <ul className="space-y-2 text-base text-foreground">
                        <li>• Volume spikes (2x average)</li>
                        <li>• Price gaps (news-driven)</li>
                        <li>• Sector strength tracking</li>
                        <li>• Setup patterns (breakouts, pullbacks)</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-panel border border-neon-purple/30">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-foreground text-xl">
                      <span className="bg-neon-purple/20 text-neon-purple border border-neon-purple w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg">3</span>
                      AI Analysis (GPT-5.2)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="mb-3 text-foreground text-base">Final evaluation with conviction scoring</p>
                    <div className="bg-background/50 border border-neon-purple/20 p-6 rounded-lg">
                      <ul className="space-y-2 text-base text-foreground">
                        <li>• Risk/reward analysis</li>
                        <li>• Pattern recognition</li>
                        <li>• Market context evaluation</li>
                        <li>• <strong className="text-neon-purple">Conviction score (1-10)</strong></li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Position Sizing Sub-Tab */}
              <TabsContent value="position" className="space-y-4">
                <Card className="glass-panel border border-neon-cyan/30">
                  <CardHeader>
                    <CardTitle className="text-neon-cyan text-2xl uppercase tracking-wider">Smart Position Sizing</CardTitle>
                    <CardDescription className="text-muted-foreground text-base">Bet BIG on high-conviction, SMALL on speculative</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-success/10 border border-success p-6 rounded-lg">
                      <h4 className="font-bold mb-2 text-success text-lg">9-10 Conviction (Slam Dunk)</h4>
                      <p className="text-base mb-1 text-foreground">18-20% of budget → <strong className="text-success">$900-$1,000</strong></p>
                      <p className="text-sm text-muted-foreground">All factors perfect, extremely high probability</p>
                    </div>
                    <div className="bg-neon-cyan/10 border border-neon-cyan p-6 rounded-lg">
                      <h4 className="font-bold mb-2 text-neon-cyan text-lg">7-8 Conviction (Strong Setup)</h4>
                      <p className="text-base mb-1 text-foreground">13-15% of budget → <strong className="text-neon-cyan">$650-$750</strong></p>
                      <p className="text-sm text-muted-foreground">Good setup with minor concerns</p>
                    </div>
                    <div className="bg-warning/10 border border-warning p-6 rounded-lg">
                      <h4 className="font-bold mb-2 text-warning text-lg">5-6 Conviction (Decent)</h4>
                      <p className="text-base mb-1 text-foreground">8-10% of budget → <strong className="text-warning">$400-$500</strong></p>
                      <p className="text-sm text-muted-foreground">Okay setup, not ideal</p>
                    </div>
                    <div className="bg-neon-purple/10 border border-neon-purple p-6 rounded-lg">
                      <h4 className="font-bold mb-2 text-neon-purple text-lg">3-4 Conviction (Speculative)</h4>
                      <p className="text-base mb-1 text-foreground">4-6% of budget → <strong className="text-neon-purple">$200-$300</strong></p>
                      <p className="text-sm text-muted-foreground">Lottery ticket, high risk</p>
                    </div>
                    <div className="bg-destructive/10 border border-destructive p-6 rounded-lg">
                      <h4 className="font-bold mb-2 text-destructive text-lg">1-2 Conviction (Skip)</h4>
                      <p className="text-base mb-1 text-foreground"><strong>No trade</strong></p>
                      <p className="text-sm text-muted-foreground">Too risky, pass</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-panel border border-neon-cyan/30">
                  <CardHeader>
                    <CardTitle className="text-foreground text-xl">Safety Limits</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 text-base text-foreground">
                      <li>• <strong className="text-neon-cyan">Min Position:</strong> $100 (don't go smaller)</li>
                      <li>• <strong className="text-neon-cyan">Max Position:</strong> 25% of budget ($1,250)</li>
                      <li>• <strong className="text-neon-cyan">Daily Budget:</strong> Still enforced ($5,000)</li>
                      <li>• <strong className="text-neon-cyan">Max Positions:</strong> Still enforced (10/day)</li>
                    </ul>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Exit Strategy Sub-Tab */}
              <TabsContent value="exits" className="space-y-4">
                <Card className="glass-panel border border-warning/30">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-warning text-2xl uppercase tracking-wider">
                      <Target className="h-6 w-6 text-warning" />
                      Advanced Exit System
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-base">ATR-based stops, partial profits, and time exits</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-background/50 border border-neon-cyan/20 p-6 rounded-lg space-y-4">
                      <div>
                        <h4 className="font-bold text-base mb-2 text-neon-cyan">🎯 1. Partial Profit Taking (NEW)</h4>
                        <p className="text-base text-foreground">At +10%: Sell 50%, trail the rest. Locks gains while letting winners run.</p>
                      </div>
                      
                      <div>
                        <h4 className="font-bold text-base mb-2 text-neon-cyan">🛑 2. ATR-Based Stop Loss (NEW)</h4>
                        <p className="text-base text-foreground">Stop = 1.5x ATR (not fixed 5%). Adapts to volatility. 3% ATR → 4.5% stop. 5% ATR → 7.5% stop.</p>
                      </div>
                      
                      <div>
                        <h4 className="font-bold text-base mb-2 text-neon-cyan">📈 3. Trailing Stop</h4>
                        <p className="text-base text-foreground">Once profitable, trails 5% below highest price. Never moves down.</p>
                      </div>
                      
                      <div>
                        <h4 className="font-bold text-base mb-2 text-neon-cyan">⏱️ 4. Time Exit (NEW)</h4>
                        <p className="text-base text-foreground">Exit if not up 3% after 5 days. Kills dead money, redeploys capital.</p>
                      </div>
                      
                      <div>
                        <h4 className="font-bold text-base mb-2 text-neon-cyan">⏰ 5. Max Hold</h4>
                        <p className="text-base text-foreground">Hard exit after 7 days. Swing trades are 2-7 day holds.</p>
                      </div>
                    </div>
                    
                    <div className="bg-neon-cyan/10 border border-neon-cyan p-6 rounded-lg">
                      <h4 className="font-bold mb-3 text-neon-cyan text-lg">Example Trade Flow:</h4>
                      <ol className="space-y-2 text-base list-decimal list-inside text-foreground">
                        <li>Buy AAPL @ $100 (ATR = 4%)</li>
                        <li>Initial stop: $94 (1.5x ATR = 6%)</li>
                        <li>Day 3: Price $110 (+10%) → <strong className="text-success">Sell 50% at $110</strong></li>
                        <li>Trailing stop now: $104.50 (5% below $110)</li>
                        <li>Day 5: Price $115 → Stop moves to $109.25</li>
                        <li>Price drops to $110 → <strong className="text-success">Sell remaining 50%</strong></li>
                        <li><strong className="text-success">Total profit: +11.25% locked in</strong></li>
                      </ol>
                    </div>
                    
                    <div className="bg-success/10 border border-success p-5 rounded-lg">
                      <p className="text-base text-foreground">
                        <strong className="text-success">Why This Works:</strong> No whipsaws, locks profits early, frees dead capital fast.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Transparency Sub-Tab */}
              <TabsContent value="transparency" className="space-y-4">
                {/* PLACEMENT 2: Transparency Tab - Investment Style Disclaimer */}
                <Card className="glass-panel border-2 border-yellow-500/50 bg-yellow-500/10">
                  <CardHeader>
                    <CardTitle className="text-yellow-500 text-xl flex items-center gap-2">
                      <AlertCircle className="h-6 w-6" />
                      Investment Style Disclosure
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-background/50 border border-yellow-500/30 p-5 rounded-lg">
                      <h4 className="font-bold text-lg mb-3 text-yellow-500">This Is NOT for:</h4>
                      <ul className="space-y-2 text-base text-foreground">
                        <li>❌ Retirement accounts (401k, IRA, Roth IRA)</li>
                        <li>❌ Long-term buy-and-hold investing</li>
                        <li>❌ Passive income generation</li>
                        <li>❌ Building wealth over decades</li>
                        <li>❌ Money you can't afford to lose</li>
                      </ul>
                    </div>
                    
                    <div className="bg-success/10 border border-success/30 p-5 rounded-lg">
                      <h4 className="font-bold text-lg mb-3 text-success">This IS for:</h4>
                      <ul className="space-y-2 text-base text-foreground">
                        <li>✅ Active swing trading (5-30 day holds)</li>
                        <li>✅ Risk capital you can afford to lose</li>
                        <li>✅ Capturing short-term momentum moves</li>
                        <li>✅ High-frequency entry/exit decisions</li>
                        <li>✅ Volatility-based position sizing</li>
                      </ul>
                    </div>

                    <div className="bg-red-900/20 border border-red-500/30 p-5 rounded-lg">
                      <p className="text-foreground text-base">
                        <strong className="text-red-400">Risk Warning:</strong> Swing trading involves significant risk. You can lose your entire investment. 
                        Past performance does not guarantee future results. This is not financial advice. Trade at your own risk.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-panel border border-neon-purple/30">
                  <CardHeader>
                    <CardTitle className="text-neon-purple text-2xl uppercase tracking-wider">Full Transparency</CardTitle>
                    <CardDescription className="text-muted-foreground text-base">See exactly why every stock was accepted or rejected</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-foreground text-base">Go to <strong className="text-neon-cyan">Scanner Results</strong> tab to see:</p>
                    <div className="bg-background/50 border border-neon-purple/20 p-6 rounded-lg space-y-4">
                      <div>
                        <h4 className="font-bold text-base mb-2 text-neon-purple">📅 Historical Scans</h4>
                        <p className="text-base text-foreground">View last 10 scans with date selector</p>
                      </div>
                      <div>
                        <h4 className="font-bold text-base mb-2 text-neon-purple">🔎 Filters</h4>
                        <p className="text-base text-foreground">Filter by status (Bought/Passed/Rejected), rejection reason, or search by symbol</p>
                      </div>
                      <div>
                        <h4 className="font-bold text-base mb-2 text-neon-purple">📊 All 500 Stocks</h4>
                        <p className="text-base text-foreground mb-3">See every single stock scanned with exact rejection reason:</p>
                        <ul className="text-sm text-muted-foreground mt-2 space-y-1 ml-4">
                          <li>• AAPL: Rejected - Momentum 2.1% &lt; 5% min</li>
                          <li>• TSLA: Rejected - Price $245 &gt; $200 max</li>
                          <li>• PYPL: BOUGHT - Conviction 8/10</li>
                          <li>• MSFT: Rejected - No setup pattern</li>
                        </ul>
                      </div>
                    </div>
                    <div className="bg-neon-cyan/10 border border-neon-cyan p-5 rounded-lg">
                      <p className="text-base text-foreground">
                        <strong className="text-neon-cyan">Learn & Improve:</strong> Use this data to understand what the scanner looks for and tune your strategy
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* FAQ Sub-Tab */}
              <TabsContent value="faq" className="space-y-4">
                <Card className="glass-panel border border-neon-cyan/30">
                  <CardHeader>
                    <CardTitle className="text-neon-cyan text-2xl uppercase tracking-wider">Frequently Asked Questions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* PLACEMENT 3: FAQ - Is This For Long-Term Investing? */}
                    <div className="bg-yellow-500/10 border-2 border-yellow-500/50 p-5 rounded-lg">
                      <h4 className="font-bold text-lg mb-3 text-yellow-500 flex items-center gap-2">
                        <AlertCircle className="h-5 w-5" />
                        Q: Is this for long-term investing or retirement accounts?
                      </h4>
                      <p className="text-base text-foreground mb-3">
                        <strong className="text-red-400">A: NO.</strong> This bot is designed exclusively for <strong className="text-yellow-500">active swing trading</strong> with 5-30 day hold times.
                      </p>
                      <p className="text-base text-foreground mb-3">
                        <strong className="text-foreground">NOT suitable for:</strong> 401(k), IRA, Roth IRA, retirement accounts, buy-and-hold strategies, passive investing, or money you can't afford to lose.
                      </p>
                      <p className="text-base text-foreground">
                        <strong className="text-success">Perfect for:</strong> Risk capital, capturing momentum moves, high-frequency trading decisions, and active portfolio management.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-bold text-base mb-2 text-neon-cyan">Q: How often should I run the scanner?</h4>
                      <p className="text-base text-foreground">A: 1-2 times per day. Pre-scheduled for 9:35 AM and 12:30 PM ET, but trigger manually anytime.</p>
                    </div>
                    <div>
                      <h4 className="font-bold text-base mb-2 text-neon-cyan">Q: Why 0 trades sometimes?</h4>
                      <p className="text-base text-foreground">A: GOOD! AI isn't finding high-probability setups. Quality over quantity.</p>
                    </div>
                    <div>
                      <h4 className="font-bold text-base mb-2 text-neon-cyan">Q: Typical hold time?</h4>
                      <p className="text-base text-foreground">A: 3-10 days for swing trades. Trailing stop exits automatically.</p>
                    </div>
                    <div>
                      <h4 className="font-bold text-base mb-2 text-neon-cyan">Q: Can I see WHY stocks were rejected?</h4>
                      <p className="text-base text-foreground">A: YES! Scanner Results tab shows ALL 500 stocks with specific reasons.</p>
                    </div>
                    <div>
                      <h4 className="font-bold text-base mb-2 text-neon-cyan">Q: What determines position size?</h4>
                      <p className="text-base text-foreground">A: AI conviction score (1-10). High conviction = bigger position. See Position Sizing tab.</p>
                    </div>
                    <div>
                      <h4 className="font-bold text-base mb-2 text-neon-cyan">Q: What's the cost per scan?</h4>
                      <p className="text-base text-foreground">A: ~$0.10-$0.40 depending on how many stocks pass filters and get AI analysis. About $15-25/month if scanning 2x/day.</p>
                    </div>
                    <div>
                      <h4 className="font-bold text-base mb-2 text-neon-cyan">Q: What is ATR?</h4>
                      <p className="text-base text-foreground">A: Average True Range - measures how volatile a stock is. 3% ATR = stock moves 3%/day. 5% ATR = moves 5%/day (more volatile). We use it for adaptive stops: volatile stocks get wider stops (7.5%), calm stocks get tighter stops (4.5%). This prevents getting stopped out by normal price noise.</p>
                    </div>
                    <div>
                      <h4 className="font-bold text-base mb-2 text-neon-cyan">Q: Why ATR-based stops?</h4>
                      <p className="text-base text-foreground">A: Adapts to each stock's volatility. 3% ATR stock gets 4.5% stop. 5% ATR stock gets 7.5% stop. Prevents whipsaws from normal noise.</p>
                    </div>
                    <div>
                      <h4 className="font-bold text-base mb-2 text-neon-cyan">Q: Why partial profit taking?</h4>
                      <p className="text-base text-foreground">A: Sell 50% at +10% to lock gains, trail the rest. Protects against full reversals while capturing big wins.</p>
                    </div>
                    <div>
                      <h4 className="font-bold text-base mb-2 text-neon-cyan">Q: Why skip earnings?</h4>
                      <p className="text-base text-foreground">A: Earnings gaps can blow through stops overnight (20%+ moves). We trade technicals, not earnings lotteries.</p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Activity Tab - admin only */}
          {isAdmin && (
            <TabsContent value="activity" className="space-y-6" data-testid="activity-content">
              <Card className="glass-panel border-neon-cyan/30">
                <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <CardTitle className="font-mono uppercase text-neon-cyan">Login Activity</CardTitle>
                    <CardDescription>Last 90 days · success and failed attempts</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={loginHistoryFilter === 'all' ? 'default' : 'outline'}
                      onClick={() => setLoginHistoryFilter('all')}
                      data-testid="login-history-filter-all"
                    >All</Button>
                    <Button
                      size="sm"
                      variant={loginHistoryFilter === 'success' ? 'default' : 'outline'}
                      onClick={() => setLoginHistoryFilter('success')}
                      data-testid="login-history-filter-success"
                    >Success</Button>
                    <Button
                      size="sm"
                      variant={loginHistoryFilter === 'failed' ? 'default' : 'outline'}
                      onClick={() => setLoginHistoryFilter('failed')}
                      data-testid="login-history-filter-failed"
                    >Failed</Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={fetchLoginHistory}
                      disabled={loginHistoryLoading}
                      data-testid="login-history-refresh"
                    >{loginHistoryLoading ? '...' : '↻'}</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loginHistoryLoading ? (
                    <div className="text-center py-8 text-muted-foreground">Loading...</div>
                  ) : loginHistory.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground" data-testid="login-history-empty">
                      No login events recorded yet.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="login-history-table">
                        <thead>
                          <tr className="border-b border-neon-cyan/20 text-left font-mono uppercase text-xs text-muted-foreground">
                            <th className="py-2 pr-4">When</th>
                            <th className="py-2 pr-4">Username</th>
                            <th className="py-2 pr-4">Result</th>
                            <th className="py-2 pr-4">IP</th>
                            <th className="py-2 pr-4">User-Agent</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loginHistory.map((evt) => (
                            <tr key={evt.id} className="border-b border-neon-cyan/10 hover:bg-neon-cyan/5">
                              <td className="py-2 pr-4 font-mono text-xs whitespace-nowrap">
                                {evt.timestamp ? new Date(evt.timestamp).toLocaleString() : '-'}
                              </td>
                              <td className="py-2 pr-4 font-mono">{evt.username}</td>
                              <td className="py-2 pr-4">
                                {evt.success ? (
                                  <Badge className="bg-green-600 hover:bg-green-700">SUCCESS</Badge>
                                ) : (
                                  <Badge variant="destructive">
                                    FAIL{evt.reason ? ` · ${evt.reason}` : ''}
                                  </Badge>
                                )}
                              </td>
                              <td className="py-2 pr-4 font-mono text-xs">{evt.ip || '-'}</td>
                              <td className="py-2 pr-4 text-xs text-muted-foreground truncate max-w-xs" title={evt.user_agent}>
                                {evt.user_agent || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="text-xs text-muted-foreground mt-3" data-testid="login-history-count">
                        Showing {loginHistory.length} event{loginHistory.length === 1 ? '' : 's'}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

        </Tabs>
      </main>
    </div>
  );
}

export default App;